<template>
    <div v-if="currentStep === 'attributes'">
        <h2 class="gmf-header" v-text="t('comments.form_v2.information')"></h2>
        <div class="gmf-mb-4" v-text="t('comments.form_v2.information.description')"></div>

        <div class="gmf-attribute-message gmf-mb-4">
            {{ t('comments.post_review_attribute_message') }}
        </div>

        <CustomerAttributeEditor ref="attributeEditor" :showSaveButton="false" :hideIntegritySettings="true" :showCustomerSettings="false" />
    </div>

    <div v-else-if="currentStep === 'products'">
        <h2 class="gmf-header">
            {{ t('comments.thank_you_for.' + commentType) || t('comments.thank_you_for.product_comment') }}
        </h2>
        <div>
            {{ t('comments.saved_awaiting_publish.' + commentType) ||
                t('comments.saved_awaiting_publish.product_comment')}}
        </div>

        <div class="gmf-products-message gmf-mb-4">
            {{ t('comments.post_review_products_message') }}
        </div>

        <OrderedProductList :close-on-review-click="true" :show-reviewed="false" :show-not-reviewed-title="false"
            :products="computedOrderedProducts" :reviewToken="computedReviewToken" :disable-product-link="true" />
    </div>

    <div v-else-if="steps.length === 0">
        <h2 class="gmf-header">
            {{ t('comments.thank_you_for.' + commentType) || t('comments.thank_you_for.product_comment') }}
        </h2>

        <div>
            {{ t('comments.saved_awaiting_publish.' + commentType) ||
                t('comments.saved_awaiting_publish.product_comment')}}
        </div>
    </div>

    <div class="gmf-center gmf-mt-4">
        <button class="gmf-btn gmf-mr-3" :class="{
            'gmf-btn-secondary': step !== 1,
            'gmf-btn-primary': step === 1,
        }" @click="close">{{ t('modal.close') }}</button>

        <button class="gmf-btn gmf-btn-primary" @click="next" v-if="hasNextStep">
            {{ t('comments.form_v2.default.next') }}
        </button>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';

import callApi from '@/utils/callApi';
import state from '@/state';

const CustomerAttributeEditor = defineAsyncComponent(() => {
    return import(/* webpackChunkName: "stage-2" */'@/components/CustomerAttributeEditor');
});

import OrderedProductList from '@/components/OrderedProductList';

import getMyProfile from '@/utils/getMyProfile';

const t = state.t;

export default {
    components: {
        CustomerAttributeEditor,
        OrderedProductList,
    },

    emits: ['close'],

    props: {
        commentType: {
            type: String,
            required: true,
        },
        tokenProfile: {
            type: Object,
            default: null,
        },
        reviewToken: {
            type: String,
            default: null,
        },
        targetId: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            customerAttributeTypes: null,
            customerAttributes: {},
            step: 0,
            orderedProducts: [],
        }
    },

    beforeUnmount() {
        this.saveAttributes();
    },

    computed: {
        computedReviewToken() {
            if (this.orderedProducts && this.orderedProducts.length > 0) {
                // if our product list is already populated, we don't need to use the review token
                return null;
            }
            return this.reviewToken;
        },
        computedOrderedProducts() {
            if (this.orderedProducts && this.orderedProducts.length > 0) {
                return this.orderedProducts;
            }
            if (this.tokenProfile && this.tokenProfile.products) {
                return this.tokenProfile.products.filter(p => {
                    // exclude the product we just reviewed, since we haven't refreshed the product list
                    return p.id != this.targetId;
                }).map(p => {
                    return {
                        product: p,
                        hasReview: false,
                    }
                });
            }
            return [];
        },
        steps() {
            const steps = new Set();

            if (this.showAttributeEditor) {
                steps.add('attributes')
            }

            if (this.showProductStep) {
                steps.add('products')
            }

            return Array.from(steps);
        },

        currentStep() {
            return this.steps[this.step];
        },

        hasNextStep() {
            return this.step < this.steps.length - 1;
        },

        profile() {
            return state.profile;
        },

        showProductsAfterReview() {
            return state.merchantConfig.comments.product_review.showProductsAfterReview;
        },

        showProductStep() {
            return this.showProductsAfterReview && this.computedOrderedProducts.length > 0;
        },

        merchantHasCustomerAttributes() {
            if (!this.customerAttributeTypes) {
                return false;
            }
            return this.customerAttributeTypes.length > 0;
        },

        customerHasAttributes() {
            if (!this.profile) {
                return false;
            }
            return Object.keys(this.customerAttributes).length > 0;
        },

        showAttributeEditor() {
            if (!this.profile) { // Guests should not be prompted with attribute editor
                return false;
            }
            if (this.tokenProfile) {
                return false;
            }
            if (!this.merchantHasCustomerAttributes) {
                return false;
            }
            if (this.customerHasAttributes) {
                return false;
            }
            return true;
        },
    },

    methods: {
        t,

        async getOrderedProducts() {
            try {
                const res = await callApi('GET', '/v2/client/ordered-products');
                const data = await res.json();

                this.orderedProducts = data?.orderedProducts || [];
            } catch (e) {
                this.orderedProducts = [];
            }
        },

        async getMyAttributes() {
            try {
                const res = await callApi('GET', '/v1/client/my-customer-attributes');
                const json = await res.json();
                this.customerAttributeTypes = json.types;
                this.customerAttributes = json.attributes;
            } catch (e) {
                this.customerAttributeTypes = {};
                this.customerAttributes = {};
            }
        },

        next() {
            if (this.hasNextStep) {
                this.saveAttributes();
                this.step = 1;
            }
        },

        async saveAttributes() {
            if (this.steps.includes('attributes') && this.$refs.attributeEditor) {
                await this.$refs.attributeEditor.save();

                state.profile = await getMyProfile();
            }
        },

        close() {
            this.saveAttributes();
            this.$emit('close');
        }
    },

    async mounted() {
        if (this.profile) {
            await this.getOrderedProducts();

            await this.getMyAttributes();
        }
    },

}
</script>